'use client';
import { useState, useEffect } from 'react';
import { ProductCard } from '@/_components/products/productCard';

function usePageSize() {
    const getSize = () => {
        if (window.matchMedia('(max-width: 767px)').matches) return 1;
        if (window.matchMedia('(max-width: 1023px)').matches) return 3;
        return 4;
    };

    const [pageSize, setPageSize] = useState(
        () => typeof window !== 'undefined' ? getSize() : 4
    );

    useEffect(() => {
        const mqs = [
            window.matchMedia('(max-width: 767px)'),
            window.matchMedia('(max-width: 1023px)'),
        ];
        const handler = () => setPageSize(getSize());
        mqs.forEach((mq) => mq.addEventListener('change', handler));
        return () => mqs.forEach((mq) => mq.removeEventListener('change', handler));
    }, []);

    return pageSize;
}

export function SimilarProductsSection({ products = [] }) {
    const pageSize   = usePageSize();
    const [page, setPage] = useState(0);

    // reset to page 0 when pageSize changes (e.g. resize)
    useEffect(() => { setPage(0); }, [pageSize]);

    if (!products.length) return null;

    const totalPages = Math.ceil(products.length / pageSize);
    const visible    = products.slice(page * pageSize, page * pageSize + pageSize);
    const hasSlider  = products.length > pageSize;

    return (
        <div className="mt-5 mb-6" style={{ overflow: 'hidden' }}>

            {/* ── Section heading ──────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', marginBottom: '1rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{
                        width: '4px', height: '1.4rem',
                        background: 'linear-gradient(to bottom, var(--secondary-color), var(--primary-color))',
                        borderRadius: '2px', flexShrink: 0,
                    }} />
                    <h2 className="m-0 font-bold text-xl" style={{ color: 'var(--primary-color)' }}>Similar Products</h2>
                    <span style={{
                        fontSize: '0.7rem', fontWeight: 700,
                        background: 'rgba(27,184,167,0.12)', color: 'var(--secondary-color)',
                        padding: '0.15rem 0.5rem', borderRadius: '999px',
                        border: '1px solid rgba(27,184,167,0.3)',
                    }}>
                        {products.length} item{products.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {hasSlider && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--primary-color)', opacity: 0.5, marginRight: '0.2rem' }}>
                            {page + 1} / {totalPages}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            aria-label="Previous page"
                            style={{
                                width: '2rem', height: '2rem', borderRadius: '50%',
                                border: `1.5px solid ${page === 0 ? '#e2e8f0' : 'var(--secondary-color)'}`,
                                background: page === 0 ? '#f8fafc' : 'white',
                                cursor: page === 0 ? 'default' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                                opacity: page === 0 ? 0.4 : 1,
                            }}
                        >
                            <i className="pi pi-chevron-left" style={{ fontSize: '0.7rem', color: 'var(--primary-color)' }} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={page === totalPages - 1}
                            aria-label="Next page"
                            style={{
                                width: '2rem', height: '2rem', borderRadius: '50%',
                                border: `1.5px solid ${page === totalPages - 1 ? '#e2e8f0' : 'var(--secondary-color)'}`,
                                background: page === totalPages - 1 ? '#f8fafc' : 'white',
                                cursor: page === totalPages - 1 ? 'default' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s',
                                opacity: page === totalPages - 1 ? 0.4 : 1,
                            }}
                        >
                            <i className="pi pi-chevron-right" style={{ fontSize: '0.7rem', color: 'var(--primary-color)' }} />
                        </button>
                    </div>
                )}
            </div>

            {/* ── Responsive grid: 1 mobile / 3 tablet / 4 desktop ── */}
            <div className="grid m-0">
                {visible.map((p) => (
                    <div key={p._id ?? p.slug} className="col-12 md:col-4 lg:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                        <ProductCard data={p} />
                    </div>
                ))}
            </div>

            {/* ── Dot indicators ───────────────────────────────── */}
            {hasSlider && (
                <div style={{
                    display: 'flex', justifyContent: 'center',
                    gap: '0.1rem', marginTop: '0.25rem',
                }}>
                    {Array.from({ length: totalPages }).map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setPage(i)}
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={i === page ? 'true' : undefined}
                            style={{
                                minWidth: '44px', minHeight: '44px',
                                border: 'none', background: 'transparent',
                                cursor: 'pointer', padding: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <span style={{
                                width: i === page ? '1.5rem' : '0.5rem',
                                height: '0.5rem', borderRadius: '999px',
                                background: i === page ? 'var(--secondary-color)' : '#cbd5e1',
                                display: 'block',
                                transition: 'all 0.2s',
                            }} />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
